import { FormApi } from "final-form";

import { newGuid } from "@bps/common-utils";
import { dataAttribute, DataAttributes, DefaultButton } from "@bps/fluent-ui";
import {
  AddressType,
  CommunicationType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { AddressFieldValue } from "@shared-types/practice/address-field-value.type.ts";
import { CommunicationFieldValue } from "@shared-types/practice/communication-field-value.type.ts";
import { communicationComparer } from "@stores/core/models/Communication.ts";
import { useStores } from "@stores/hooks/useStores.ts";

interface Props<
  T extends {
    communications: CommunicationFieldValue[];
    addresses: AddressFieldValue[];
  }
> {
  reactFinalForm: FormApi<T>;
  formValues: T;
}

export const AddContactMethodsButton = <
  T extends {
    communications: CommunicationFieldValue[];
    addresses: AddressFieldValue[];
  }
>({
  reactFinalForm,
  formValues
}: Props<T>) => {
  const { core } = useStores();
  const country = core.tenantDetails?.country;

  const addAddress = (type: AddressType, locationCountry: string) => () => {
    return reactFinalForm.mutators.push("addresses", {
      street1: "",
      country: locationCountry,
      type,
      isNew: true,
      id: newGuid()
    } as AddressFieldValue);
  };

  const addContactMethod = (type: CommunicationType) => () => {
    const communications = formValues.communications;
    return reactFinalForm.change(
      "communications",
      [
        ...communications.map(comms => ({
          ...comms,
          isNew: false
        })),
        { type, isNew: true, id: newGuid() }
      ].sort(communicationComparer)
    );
  };
  return (
    <DefaultButton
      {...dataAttribute(DataAttributes.Element, "add-more-contacts")}
      iconProps={{ iconName: "Add" }}
      text="Add more"
      menuProps={{
        calloutProps: {
          ...dataAttribute(DataAttributes.Element, "add-more-contacts-list")
        },
        styles: { container: { width: 152 } },
        shouldFocusOnMount: true,
        items: [
          {
            key: "mobile",
            text: "Mobile",
            onClick: addContactMethod(CommunicationType.Mobile)
          },
          {
            key: "homePhone",
            text: "Home phone",
            onClick: addContactMethod(CommunicationType.HomePhone)
          },
          {
            key: "workPhone",
            text: "Work phone",
            onClick: addContactMethod(CommunicationType.WorkPhone)
          },
          {
            key: "afterHoursPhone",
            text: "After hours phone",
            onClick: addContactMethod(CommunicationType.AfterHours)
          },
          {
            key: "email",
            text: "Email",
            onClick: addContactMethod(CommunicationType.Email)
          },
          {
            key: "fax",
            text: "Fax",
            onClick: addContactMethod(CommunicationType.Fax)
          },
          {
            key: "physicalAddress",
            text: "Physical address",
            onClick: addAddress(AddressType.Physical, country ?? "")
          },
          {
            key: "postalAddress",
            text: "Postal address",
            onClick: addAddress(AddressType.Postal, country ?? "")
          }
        ]
      }}
    />
  );
};
